// src/components/common/ShiftClockOutPrompt.js
// shiftprompt-9b1e6c37
//
// The self-reported clock-out prompt. Used by BOTH stale branches:
//   - clock-OUT of a shift open >= STALE_MS ("when did this shift end?")
//   - clock-IN while an old stale shift is still open ("finish your last one")
// The parent (CrmApp) owns the Firestore writes; this component owns only the
// time entry, validation, and error copy. All copy + validation come from
// src/utils/shiftGuard.js so web and mobile stay in lockstep.
//
// The picker opens AT the shift's start, never at "now" — submitting the
// unchanged default fails validation (END_BEFORE_START), so the answer is
// never auto-filled. "I finished just now" fills the current time as a
// DELIBERATE selection (needed for genuine long overnight shifts).

import React, { useState, useEffect } from 'react';
import { X, Clock, AlertTriangle } from 'lucide-react';
import {
  CLOCK_OUT_STALE_TITLE, CLOCK_OUT_STALE_BODY,
  CLOCK_IN_STALE_TITLE, CLOCK_IN_STALE_BODY,
  FINISHED_NOW_LABEL, SUBMIT_LABEL, CANCEL_LABEL, ERROR_COPY,
  validateSelfReportedEnd,
} from '../../utils/shiftGuard';

// Firestore Timestamp | null -> "YYYY-MM-DDTHH:mm" in LOCAL time (datetime-local
// format), or '' when there's no usable start (corrupt doc).
const toLocalInputValue = (ts) => {
  const d = ts && typeof ts.toDate === 'function' ? ts.toDate() : (ts instanceof Date ? ts : null);
  if (!d || isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const nowLocalInputValue = () => toLocalInputValue(new Date());

const ShiftClockOutPrompt = ({ open, mode, shiftStart, nextShiftStart, onSubmit, onCancel }) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Re-seed the input at the shift's start every time the prompt opens.
  useEffect(() => {
    if (open) {
      setValue(toLocalInputValue(shiftStart));
      setError(null);
      setSubmitting(false);
    }
  }, [open, shiftStart]);

  if (!open) return null;

  const isClockIn = mode === 'clockin';
  const title = isClockIn ? CLOCK_IN_STALE_TITLE : CLOCK_OUT_STALE_TITLE;
  const body = isClockIn ? CLOCK_IN_STALE_BODY : CLOCK_OUT_STALE_BODY;

  // datetime-local "YYYY-MM-DDTHH:mm" parses as LOCAL time.
  const enteredMs = value ? new Date(value).getTime() : null;

  const handleSave = async () => {
    if (submitting) return;
    const err = validateSelfReportedEnd(shiftStart, enteredMs, Date.now(), nextShiftStart);
    if (err) { setError(err); return; }
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit(enteredMs);
    } catch (e) {
      // Parent handles its own guard release; surface a generic failure here.
      setSubmitting(false);
      setError('END_REQUIRED'); // fallback copy; real errors are validated above
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Clock size={18} className="text-[#5F4EC9]" />
            {title}
          </h3>
          <button
            onClick={onCancel}
            disabled={submitting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            aria-label="Cancel"
          >
            <X size={20} />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">{body}</p>

        <label className="block text-xs font-semibold text-gray-500 mb-1">
          Clock-out time
        </label>
        <input
          type="datetime-local"
          value={value}
          onChange={(e) => { setValue(e.target.value); setError(null); }}
          disabled={submitting}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5F4EC9] mb-2"
        />

        <button
          type="button"
          onClick={() => { setValue(nowLocalInputValue()); setError(null); }}
          disabled={submitting}
          className="text-sm text-[#5F4EC9] font-semibold hover:underline disabled:opacity-50 mb-4"
        >
          {FINISHED_NOW_LABEL}
        </button>

        {error && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm mb-4">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span>{ERROR_COPY[error]}</span>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-50"
          >
            {CANCEL_LABEL}
          </button>
          <button
            onClick={handleSave}
            disabled={submitting || !value}
            className="px-4 py-2 rounded-lg text-sm font-bold text-white bg-[#5F4EC9] hover:bg-[#4d3fa8] disabled:opacity-50"
          >
            {submitting ? 'Saving…' : SUBMIT_LABEL}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShiftClockOutPrompt;
